/**
 * ActivityLogView — Operations rendered as a proper data table.
 *
 * Matches UX mock View 7 with columns: Operation, Type, Rows, Review, Status, Actions.
 * Promoted rows get a subtle blue background. Supports promote/discard actions.
 * Includes filter chips (All / Pipeline / Exploration) and exploratory toggle.
 */
import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as PassIcon,
  Build as FixedIcon,
  WarningAmber as WarningIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  DeleteOutline as DeleteIcon,
  Science as ExploreIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import type { WorkspaceOperation, TablePreview } from '../../services/workspaceApi';
import { previewWorkspaceTable } from '../../services/workspaceApi';
import { formatCheckSummaryLine } from './reviewSummaryUtils';
import InlineResultTable from './InlineResultTable';

// ── Types ────────────────────────────────────────────────────────────

type ActivityFilter = 'all' | 'pipeline' | 'exploration';

interface ActivityLogViewProps {
  operations: WorkspaceOperation[];
  sessionId: string;
  sessionLabel?: string;
  onDiscard?: (operationId: string) => void;
  onSetExploratory?: (operationId: string, exploratory: boolean) => void;
  onOpenInExplorer?: (tableName: string) => void;
}

// ── Constants ────────────────────────────────────────────────────────

const TRANSFORMATIVE_TYPES = new Set([
  'sql_transform', 'python_transform', 'fuzzy_match',
  'llm_extract', 'llm_classify', 'api_enrich', 'validation',
  'hierarchical_assembly',
]);

// ── Helpers ──────────────────────────────────────────────────────────

/** Color map for operation type badges. */
const TYPE_COLORS: Record<string, string> = {
  inspect: '#6366f1',       // indigo
  query: '#6366f1',         // indigo
  profile: '#6366f1',       // indigo
  compare: '#6366f1',       // indigo
  sql_transform: '#0891b2', // cyan
  python_transform: '#059669', // emerald
  fuzzy_match: '#d97706',   // amber
  llm_extract: '#7c3aed',   // violet
  llm_classify: '#7c3aed',  // violet
  api_enrich: '#2563eb',    // blue
  validation_gate: '#dc2626', // red
};

/** Friendly label for operation types. */
const TYPE_LABELS: Record<string, string> = {
  inspect: 'INSPECT',
  query: 'QUERY',
  profile: 'PROFILE',
  compare: 'COMPARE',
  sql_transform: 'SQL_TRANSFORM',
  python_transform: 'PYTHON',
  fuzzy_match: 'FUZZY_MATCH',
  llm_extract: 'LLM_EXTRACT',
  llm_classify: 'LLM_CLASSIFY',
  api_enrich: 'API_ENRICH',
  validation_gate: 'VALIDATE',
  hierarchical_assembly: 'ASSEMBLY',
};

/** Status chip color mapping. */
const STATUS_CHIP: Record<string, { color: 'primary' | 'success' | 'default' | 'warning' | 'error'; label: string }> = {
  executed: { color: 'primary', label: 'executed' },
  promoted: { color: 'success', label: 'promoted' },
  discarded: { color: 'default', label: 'discarded' },
  corrected: { color: 'warning', label: 'corrected' },
  failed: { color: 'error', label: 'failed' },
};

/** Compute review result summary. */
function getReviewSummary(
  op: WorkspaceOperation,
): { label: string; color: 'success' | 'info' | 'warning' | 'default'; icon: React.ReactElement; checkDetails: string | null } | null {
  const review = op.review_result;
  if (!review) return null;

  const checkCount = review.checks?.length ?? 0;
  const checkDetails = formatCheckSummaryLine(review.checks, review.corrections);

  if (review.passed && review.corrections.length === 0 && review.warnings.length === 0) {
    return { label: checkCount > 0 ? `Pass (${checkCount})` : 'Pass', color: 'success', icon: <PassIcon sx={{ fontSize: 14 }} />, checkDetails };
  }
  if (review.corrections.length > 0 && review.corrections.some((c) => c.succeeded)) {
    return { label: checkCount > 0 ? `Fixed (${checkCount})` : 'Fixed', color: 'info', icon: <FixedIcon sx={{ fontSize: 14 }} />, checkDetails };
  }
  if (review.warnings.length > 0) {
    return { label: checkCount > 0 ? `Warning (${checkCount})` : 'Warning', color: 'warning', icon: <WarningIcon sx={{ fontSize: 14 }} />, checkDetails };
  }
  return { label: checkCount > 0 ? `Pass (${checkCount})` : 'Pass', color: 'success', icon: <PassIcon sx={{ fontSize: 14 }} />, checkDetails };
}

/** Format row count delta. */
function formatRowDelta(before?: number | null, after?: number | null): string {
  if (before == null && after == null) return '-';
  if (before != null && after != null) {
    const delta = after - before;
    const sign = delta > 0 ? '+' : '';
    return `${before.toLocaleString()} \u2192 ${after.toLocaleString()} (${sign}${delta.toLocaleString()})`;
  }
  if (after != null) return `${after.toLocaleString()} rows`;
  if (before != null) return `${before.toLocaleString()} rows`;
  return '-';
}

/** Format timestamp to short form. */
function shortTime(ts?: string): string {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
}

// ── Component ────────────────────────────────────────────────────────

/** Extract the executable code from an operation's config. */
function getOperationCode(op: WorkspaceOperation): string | null {
  const cfg = op.config;
  if (!cfg) return null;
  if (typeof cfg.sql === 'string') return cfg.sql;
  if (typeof cfg.function_code === 'string') return cfg.function_code;
  if (typeof cfg.code === 'string') return cfg.code;
  if (typeof cfg.query === 'string') return cfg.query;
  return null;
}

const ActivityLogView: React.FC<ActivityLogViewProps> = ({
  operations,
  sessionId,
  sessionLabel,
  onDiscard,
  onSetExploratory,
  onOpenInExplorer,
}) => {
  const theme = useTheme();
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [expandedOpId, setExpandedOpId] = useState<string | null>(null);
  const [previewCache, setPreviewCache] = useState<Record<string, TablePreview>>({});
  const [loadingPreviews, setLoadingPreviews] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<string | null>(null);

  const handleToggleExpand = useCallback((opId: string, outputRef?: string | null) => {
    setExpandedOpId((prev) => {
      const next = prev === opId ? null : opId;
      // Lazy-load preview when expanding
      if (next && outputRef && !previewCache[opId] && !loadingPreviews.has(opId)) {
        setLoadingPreviews((s) => new Set(s).add(opId));
        previewWorkspaceTable(sessionId, outputRef, 5)
          .then((data) => {
            setPreviewCache((c) => ({ ...c, [opId]: data }));
          })
          .catch(() => { /* preview unavailable — that's fine */ })
          .finally(() => {
            setLoadingPreviews((s) => {
              const ns = new Set(s);
              ns.delete(opId);
              return ns;
            });
          });
      }
      return next;
    });
  }, [sessionId, previewCache, loadingPreviews]);

  const handleCopy = useCallback((opId: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(opId);
      setTimeout(() => setCopied((c) => (c === opId ? null : c)), 1500);
    });
  }, []);

  if (operations.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No operations yet. Start exploring data to see operations here.
        </Typography>
      </Box>
    );
  }

  // Apply filter
  const filteredOps = operations.filter((op) => {
    if (filter === 'pipeline') {
      return TRANSFORMATIVE_TYPES.has(op.operation_type) && !op.exploratory && op.status !== 'discarded';
    }
    if (filter === 'exploration') {
      return op.exploratory || !TRANSFORMATIVE_TYPES.has(op.operation_type);
    }
    return true; // 'all'
  });

  // Counts for filter chips
  const pipelineCount = operations.filter(
    (op) => TRANSFORMATIVE_TYPES.has(op.operation_type) && !op.exploratory && op.status !== 'discarded',
  ).length;
  const explorationCount = operations.filter(
    (op) => op.exploratory || !TRANSFORMATIVE_TYPES.has(op.operation_type),
  ).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          {sessionLabel && (
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {sessionLabel}
            </Typography>
          )}
          <Chip
            label={`All (${operations.length})`}
            size="small"
            variant={filter === 'all' ? 'filled' : 'outlined'}
            color={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
            sx={{ fontSize: '0.6rem', height: 20, cursor: 'pointer' }}
          />
          <Chip
            label={`Pipeline (${pipelineCount})`}
            size="small"
            variant={filter === 'pipeline' ? 'filled' : 'outlined'}
            color={filter === 'pipeline' ? 'primary' : 'default'}
            onClick={() => setFilter('pipeline')}
            sx={{ fontSize: '0.6rem', height: 20, cursor: 'pointer' }}
          />
          <Chip
            label={`Exploration (${explorationCount})`}
            size="small"
            variant={filter === 'exploration' ? 'filled' : 'outlined'}
            color={filter === 'exploration' ? 'primary' : 'default'}
            onClick={() => setFilter('exploration')}
            sx={{ fontSize: '0.6rem', height: 20, cursor: 'pointer' }}
          />
        </Stack>
      </Box>

      {/* Compact card list */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 0.5 }}>
        {filteredOps.map((op) => {
          const typeColor = TYPE_COLORS[op.operation_type] || '#6b7280';
          const typeLabel = TYPE_LABELS[op.operation_type] || op.operation_type.toUpperCase();
          const statusInfo = STATUS_CHIP[op.status] || { color: 'default' as const, label: op.status };
          const reviewSummary = getReviewSummary(op);
          const isDiscarded = op.status === 'discarded';
          const isExploratory = op.exploratory === true;
          const isTransformative = TRANSFORMATIVE_TYPES.has(op.operation_type);

          const isExpanded = expandedOpId === op.operation_id;
          const code = getOperationCode(op);
          const preview = previewCache[op.operation_id];
          const isLoadingPreview = loadingPreviews.has(op.operation_id);

          return (
            <Box
              key={op.operation_id}
              sx={{
                p: 1,
                mb: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: isExpanded
                  ? alpha(theme.palette.primary.main, 0.3)
                  : isExploratory
                    ? alpha(theme.palette.info.main, 0.2)
                    : 'divider',
                borderStyle: isExploratory ? 'dashed' : 'solid',
                bgcolor: isDiscarded
                  ? alpha(theme.palette.grey[500], 0.04)
                  : isExploratory
                    ? alpha(theme.palette.info.main, 0.02)
                    : 'background.paper',
                opacity: isDiscarded ? 0.6 : 1,
                cursor: 'pointer',
                transition: 'border-color 0.15s',
              }}
              onClick={() => handleToggleExpand(op.operation_id, op.output_ref)}
            >
              {/* Row 1: Chevron + Description + Status + Exploratory badge */}
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.5}>
                <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                  {isExpanded
                    ? <ExpandLessIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                    : <ExpandMoreIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                  }
                  <Typography variant="body2" sx={{ fontSize: 11, fontWeight: 500, lineHeight: 1.4, flex: 1 }} noWrap>
                    {op.description || op.operation_type}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                  {isExploratory && (
                    <Chip
                      icon={<ExploreIcon sx={{ fontSize: 10 }} />}
                      label="exploration"
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{ fontSize: '0.5rem', height: 16, '& .MuiChip-icon': { fontSize: 10 } }}
                    />
                  )}
                  <Chip
                    label={statusInfo.label}
                    size="small"
                    color={statusInfo.color}
                    variant="outlined"
                    sx={{ fontSize: '0.55rem', height: 18 }}
                  />
                </Stack>
              </Stack>

              {/* Row 2: Type badge + Rows + Review + Actions */}
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                <Chip
                  label={typeLabel}
                  size="small"
                  sx={{
                    fontFamily: '"Fira Code", monospace',
                    fontSize: '0.55rem',
                    height: 18,
                    fontWeight: 600,
                    bgcolor: alpha(typeColor, 0.1),
                    color: typeColor,
                    border: 'none',
                  }}
                />
                {op.row_count_after != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                    {op.row_count_after.toLocaleString()} rows
                  </Typography>
                )}
                {reviewSummary && (
                  <Chip
                    icon={reviewSummary.icon}
                    label={reviewSummary.label}
                    size="small"
                    color={reviewSummary.color}
                    variant="outlined"
                    sx={{ fontSize: '0.55rem', height: 18, '& .MuiChip-icon': { fontSize: 12 } }}
                  />
                )}
                <Box sx={{ flex: 1 }} />
                {/* Toggle exploratory */}
                {onSetExploratory && isTransformative && op.status !== 'discarded' && (
                  <Tooltip title={isExploratory ? 'Show in pipeline' : 'Hide from pipeline'}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onSetExploratory(op.operation_id, !isExploratory); }}
                      sx={{ p: 0.25, color: isExploratory ? 'info.main' : 'text.disabled' }}
                    >
                      {isExploratory ? <ViewIcon sx={{ fontSize: 14 }} /> : <HideIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Tooltip>
                )}
                {onDiscard && op.status !== 'discarded' && (
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDiscard(op.operation_id); }} sx={{ p: 0.25 }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {/* Row 3: Quality check details */}
              {reviewSummary?.checkDetails && (
                <Box
                  sx={{
                    mt: 0.5,
                    px: 0.75,
                    py: 0.25,
                    bgcolor: alpha(
                      reviewSummary.color === 'success' ? '#22c55e' :
                      reviewSummary.color === 'info' ? '#3b82f6' :
                      reviewSummary.color === 'warning' ? '#f59e0b' : '#6b7280',
                      0.06,
                    ),
                    borderRadius: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      fontSize: 9.5,
                      lineHeight: 1.3,
                      color: 'text.secondary',
                      display: 'block',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {reviewSummary.checkDetails}
                  </Typography>
                </Box>
              )}

              {/* Expanded section: Code block + Result preview */}
              <Collapse in={isExpanded} unmountOnExit>
                <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                  {/* Code block */}
                  {code && (
                    <Box sx={{ position: 'relative', mb: 1 }}>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.5,
                          pr: 4,
                          borderRadius: 1,
                          bgcolor: '#1e293b',
                          color: '#e2e8f0',
                          overflowX: 'auto',
                          fontSize: 11,
                          lineHeight: 1.5,
                          fontFamily: '"Fira Code", "Roboto Mono", monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxHeight: 200,
                        }}
                      >
                        {code}
                      </Box>
                      <Tooltip title={copied === op.operation_id ? 'Copied!' : 'Copy'}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(op.operation_id, code)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            color: copied === op.operation_id ? '#22c55e' : 'rgba(255,255,255,0.5)',
                            '&:hover': { color: '#fff' },
                          }}
                        >
                          <CopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}

                  {/* Error display */}
                  {op.error && (
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.error.main, 0.06),
                        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                        mb: 1,
                      }}
                    >
                      <Typography variant="caption" color="error" sx={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
                        {op.error}
                      </Typography>
                    </Box>
                  )}

                  {/* Result preview */}
                  {op.output_ref && !op.error && (
                    <>
                      {isLoadingPreview && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 1 }}>
                          <CircularProgress size={12} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            Loading preview...
                          </Typography>
                        </Box>
                      )}
                      {preview && (
                        <InlineResultTable
                          columns={preview.columns}
                          rows={preview.rows.map((row) => preview.columns.map((col) => row[col]))}
                          maxRows={5}
                          totalRows={preview.row_count}
                        />
                      )}
                    </>
                  )}

                  {/* Open in Explorer button */}
                  {op.output_ref && onOpenInExplorer && (
                    <Button
                      size="small"
                      endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                      onClick={() => onOpenInExplorer(op.output_ref!)}
                      sx={{ fontSize: 10, textTransform: 'none', mt: 0.5, p: 0.25, minHeight: 0 }}
                    >
                      Open in Explorer
                    </Button>
                  )}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ActivityLogView;
